/* eslint-disable no-use-before-define */
import { addRxPlugin, createRxDatabase, RxConflictHandler, RxStorage } from 'rxdb';
import { isEqual } from 'lodash';
import { getRxStorageIndexedDB } from 'rxdb-premium/plugins/storage-indexeddb';
import { getRxStorageMemory } from 'rxdb/plugins/storage-memory';
import { getAjv, wrappedValidateAjvStorage } from 'rxdb/plugins/validate-ajv';
import { RxDBDevModePlugin, disableWarnings } from 'rxdb/plugins/dev-mode';
import { RxDBCleanupPlugin } from 'rxdb/plugins/cleanup';
import { RxDBLeaderElectionPlugin } from 'rxdb/plugins/leader-election';
import { RxDBLocalDocumentsPlugin } from 'rxdb/plugins/local-documents';
import { RxDBMigrationSchemaPlugin } from 'rxdb/plugins/migration-schema';
import addAjvFormats from 'ajv-formats';
import Bluebird from 'bluebird';

import type { RxNoodleCollections, RxNoodleDB, RxNoodleDBCollectionsKeys } from './rxdb.types';
import { getAvailableSpaceInMB, rxdbError, rxdbLog } from './rxdb.tools';
import {
  CURRENT_RESET_NUMBER,
  CURRENT_RXDB_SUFFIX_KEY,
  IS_DEV_MODE,
  MINIMUM_AVAILABLE_MEMORY_IN_MB,
  PREVIOUS_RXDB_PREFIX,
  RESET_DB_KEY,
  RXDB_PREFIX,
} from './rxdb.constants';
import { questionnaireFieldTemplateSchema } from './schemas/questionnaireFieldTemplate.schema';
import { questionnaireTemplateSchema } from './schemas/questionnaireTemplate.schema';
import { qreFieldTemplateSchema } from './schemas/qreFieldTemplate.schema';
import { qreTemplateOfQreSchema } from './schemas/qreTemplateOfQre.schema';
import { questionnaireResponseSchema } from './schemas/questionnaireResponse.schema';

if (IS_DEV_MODE) {
  disableWarnings();
  addRxPlugin(RxDBDevModePlugin);
}

addRxPlugin(RxDBLeaderElectionPlugin);
addRxPlugin(RxDBCleanupPlugin);
addRxPlugin(RxDBLocalDocumentsPlugin);
addRxPlugin(RxDBMigrationSchemaPlugin);

if (typeof window !== 'undefined') {
  const ajv = getAjv();
  // @ts-expect-error - Known type mismatch between ajv versions
  addAjvFormats(ajv);
}

function getRxInMemoryStorage(): RxStorage<unknown, unknown> {
  return wrappedValidateAjvStorage({
    storage: getRxStorageMemory(),
  });
}

async function getRxStorage(): Promise<RxStorage<unknown, unknown>> {
  const availableMemory = await getAvailableSpaceInMB();

  if (availableMemory !== -1 && availableMemory < MINIMUM_AVAILABLE_MEMORY_IN_MB) {
    rxdbLog(`Available memory is less than ${MINIMUM_AVAILABLE_MEMORY_IN_MB}MB, available: ${availableMemory}MB, using in memory storage`);
    return getRxInMemoryStorage();
  }

  return wrappedValidateAjvStorage({
    storage: getRxStorageIndexedDB(),
  });
}

function handleAssumedMasterDocsConflictHandler(): RxConflictHandler<unknown> {
  const conflictHandler: RxConflictHandler<unknown> = {
    isEqual(_a, _b, context) {
      if (context === 'downstream-check-if-equal-0') return true;
      return isEqual(_a, _b);
    },
    resolve(i) {
      return Promise.resolve(i.realMasterState);
    },
  };

  return conflictHandler;
}

async function initializeCollections(db: RxNoodleDB): Promise<boolean> {
  try {
    await db.addCollections({
      qreFieldTemplate: {
        migrationStrategies: {},
        schema: qreFieldTemplateSchema,
      },
      qreTemplateOfQre: {
        migrationStrategies: {},
        schema: qreTemplateOfQreSchema,
      },
      questionnaireFieldTemplate: {
        conflictHandler: handleAssumedMasterDocsConflictHandler(),
        migrationStrategies: {},
        schema: questionnaireFieldTemplateSchema,
      },
      questionnaireResponse: {
        migrationStrategies: {},
        schema: questionnaireResponseSchema,
      },
      questionnaireTemplate: {
        migrationStrategies: {},
        schema: questionnaireTemplateSchema,
      },
    });

    return true;
  } catch (error) {
    rxdbError('Error initializing collections', error);
    return false;
  }
}

/**
 * Purges the data of the collections.
 *
 * @returns {Promise<boolean>} - True if the data was purged, false if an error occurred.
 */
export async function purgeCollectionsData(
  db: RxNoodleDB,
  collections: RxNoodleDBCollectionsKeys[] = ['qreFieldTemplate', 'qreTemplateOfQre'],
): Promise<boolean> {
  try {
    await Bluebird.mapSeries(collections, async collection => {
      const deleteRecursively = async (): Promise<void> => {
        const now = Date.now();
        rxdbLog(`Deleting documents recursively from collection: ${collection}`);
        const docs = await db[collection].find({ limit: 1000 }).remove();
        rxdbLog(`Deleted ${docs.length} documents from collection: ${collection} in ${Date.now() - now}ms`);
        if (docs.length > 0) {
          await deleteRecursively();
        }
      };

      rxdbLog(`Purging data from collection: ${collection}`);
      await deleteRecursively();
      rxdbLog(`Data purged from collection: ${collection}`);
      await db[collection].cleanup(0);
      rxdbLog(`Cleanup completed for collection: ${collection}`);
    });

    return true;
  } catch (error) {
    rxdbError('Error purging collections data', error);
    return false;
  }
}

function buildDBName({ creatorId, userId, currentSuffixNumber }: { creatorId: string; userId: string; currentSuffixNumber: string }): string {
  return `${RXDB_PREFIX}-${creatorId}-${userId}-${currentSuffixNumber}`;
}

function shouldResetRxNoodleDB(): boolean {
  try {
    const hasBeenReset = localStorage.getItem(RESET_DB_KEY);
    const shouldReset = hasBeenReset !== CURRENT_RESET_NUMBER;
    return shouldReset;
  } catch (error) {
    rxdbError('Error checking reset status', error);
    return false;
  }
}

function getCurrentRxDBSuffixNumber(): string {
  let currentSuffixNumber = localStorage.getItem(CURRENT_RXDB_SUFFIX_KEY);

  if (!currentSuffixNumber) {
    currentSuffixNumber = '1';
    localStorage.setItem(CURRENT_RXDB_SUFFIX_KEY, currentSuffixNumber);
  }

  return currentSuffixNumber;
}

function incrementCurrentRxDBSuffixNumber(): string {
  const currentSuffixNumber = getCurrentRxDBSuffixNumber();
  const newSuffixNumber = (Number(currentSuffixNumber) + 1).toString();

  localStorage.setItem(CURRENT_RXDB_SUFFIX_KEY, newSuffixNumber);

  return newSuffixNumber;
}

function checkCurrentRxDBStatusAndGetSuffixNumber({ creatorId, userId }: { creatorId: string; userId: string }): string | null {
  try {
    let currentSuffixNumber = getCurrentRxDBSuffixNumber();

    // Check if the database should be reset
    const shouldReset = shouldResetRxNoodleDB();
    if (shouldReset) {
      currentSuffixNumber = incrementCurrentRxDBSuffixNumber();
      markRxNoodleDBAsReset();

      removeAllRxDBs({ avoidRemoveFor: { creatorId, currentSuffixNumber, userId }, wait: 10000 });
    }

    // Return the up to date suffix number
    return currentSuffixNumber;
  } catch (error) {
    rxdbError('Error checking current RxDB status', error);
    return null;
  }
}

function markRxNoodleDBAsReset(): void {
  try {
    localStorage.setItem(RESET_DB_KEY, CURRENT_RESET_NUMBER);
  } catch (error) {
    rxdbError('Error marking reset status', error);
  }
}

type InitializationConfig = {
  creatorId: string;
  userId: string;
};

/**
 * Initializes the RxNoodleDB instance.
 *
 * @returns {Promise<RxNoodleDB>} - The initialized RxNoodleDB instance.
 */
async function initializeRxNoodleDB({ creatorId, userId }: InitializationConfig): Promise<RxNoodleDB> {
  const currentSuffixNumber = checkCurrentRxDBStatusAndGetSuffixNumber({ creatorId, userId });

  if (!currentSuffixNumber) {
    throw new Error('Failed to get current RxDB suffix number');
  }

  const dbName = buildDBName({ creatorId, currentSuffixNumber, userId });

  rxdbLog(`Initializing database: ${dbName}`);

  const storage = await getRxStorage();

  return createRxDatabase<RxNoodleCollections>({
    cleanupPolicy: {
      minimumDeletedTime: 0,
    },
    localDocuments: true,
    name: dbName,
    storage,
  })
    .then(db => {
      rxdbLog(`Database initialized: ${dbName}`);
      rxdbLog('Initializing collections');

      return initializeCollections(db).then(successfullyAddedCollections => {
        if (!successfullyAddedCollections) throw new Error('Failed to initialize collections');

        rxdbLog('Collections initialized');

        return db;
      });
    })
    .catch(error => {
      rxdbError('Error initializing RxNoodleDB', error);
      throw error;
    });
}

let rxNoodleDBPromise: Promise<RxNoodleDB> | null = null;

/**
 * Retrieves the RxNoodleDB instance.
 *
 * @returns {Promise<RxNoodleDB>} - The RxNoodleDB instance.
 */
export function getNoodleDb(initConfig?: InitializationConfig): Promise<RxNoodleDB> {
  if (!rxNoodleDBPromise && initConfig) {
    rxNoodleDBPromise = initializeRxNoodleDB(initConfig);
  } else if (!rxNoodleDBPromise) {
    throw new Error('The rxdb database is not initialized and no initialization config was provided');
  }

  return rxNoodleDBPromise;
}

/**
 * Closes the RxNoodleDB instance and set the promise to null.
 * Used when the creator ID changes.
 */
export async function closeCurrentRxNoodleDB(): Promise<void> {
  if (!rxNoodleDBPromise) return;

  const db = await rxNoodleDBPromise;
  rxdbLog(`Closing database: ${db.name}`);
  await db.close();
  rxdbLog(`Database closed: ${db.name}`);

  rxNoodleDBPromise = null;
}

/**
 * Removes all RxNoodleDB databases.
 * All databases which start with the prefix 'RXDB_PREFIX' will be removed.
 */
async function removeAllRxDBs({
  avoidRemoveFor: { creatorId, userId, currentSuffixNumber } = {},
  wait = 0,
}: {
  avoidRemoveFor?: {
    creatorId?: string;
    userId?: string;
    currentSuffixNumber?: string;
  };
  wait?: number;
} = {}): Promise<void> {
  if (!('databases' in window.indexedDB)) {
    return;
  }

  if (wait) {
    await new Promise(resolve => {
      setTimeout(() => {
        resolve(true);
      }, wait);
    });
  }

  const dbs = await window.indexedDB.databases();

  await Bluebird.mapSeries(dbs, async db => {
    const { name } = db;

    rxdbLog(`Checking if database should be removed: ${name}`);

    const isGladeDB = name?.startsWith(RXDB_PREFIX) || name?.startsWith(PREVIOUS_RXDB_PREFIX);
    if (!name || !isGladeDB) {
      rxdbLog(`Skipping removal of non-glade database: ${name}`);
      return;
    }

    if (creatorId && userId && currentSuffixNumber) {
      const isCurrentDB = name === buildDBName({ creatorId, currentSuffixNumber, userId });
      if (isCurrentDB) {
        rxdbLog(`Skipping removal of current database: ${name}`);
        return;
      }
    }

    const removeDb = async (): Promise<void> => {
      rxdbLog(`Removing database: ${name}`);
      window.indexedDB.deleteDatabase(name);
      rxdbLog(`Database removed: ${name}`);
    };

    const currentDb = await rxNoodleDBPromise;

    if (currentDb?.name === name) {
      purgeCollectionsData(currentDb, ['questionnaireFieldTemplate', 'questionnaireTemplate', 'questionnaireResponse']).then(() => {
        closeCurrentRxNoodleDB().then(() => {
          setTimeout(() => {
            removeDb();
          }, 1000);
        });
      });
    } else {
      removeDb();
    }
  });
}

/**
 * Handles the RxDB post logout user process.
 * This function will remove all RxDB databases and increment the current RxDB suffix number.
 * It's used when the user logs out.
 *
 * @returns {Promise<void>} - A promise that resolves when the RxDB post logout user process is complete.
 */
export async function handleRxDBPostLogoutUserProcess(): Promise<void> {
  try {
    incrementCurrentRxDBSuffixNumber();
    await removeAllRxDBs();
  } catch (error) {
    rxdbError('Error handling RxDB post logout user process', error);
    throw error;
  }
}
